import React, { useContext } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { FindifyMenuContext } from "@/contexts/FindifyMenuContext";
import { PlaceColors } from "../helpers/PlaceColors";
import { PlaceText } from "./PlaceText";
import { toggleSidemenu } from "./FindifyNavigation";

type FindifyAppHeaderProps = {
    navigation: any,
    findifyDataContext: FindifyDataContextType | null,
    rightIcon?: string,
    onRightPress?: () => void,
    showAccessibility?: boolean,
    allowBackFallback?: boolean,
}

export const FindifyAppHeader = ({
    navigation,
    findifyDataContext,
    rightIcon = "qr-code-outline",
    onRightPress,
    showAccessibility,
    allowBackFallback = true,
}: FindifyAppHeaderProps) => {
    const findifyMenuContext = useContext(FindifyMenuContext);
    const colors = PlaceColors(findifyDataContext);
    const logo = findifyDataContext?.workspace?.app_settings?.app_logo_url;
    const isMuseumMode = !!findifyMenuContext?.activeMuseumSlug;
    const museumTitle = findifyMenuContext?.activeMuseumTitle || findifyMenuContext?.activeMuseumSlug;
    const canOpenFullMenu = findifyMenuContext?.isSidemenu
        || findifyMenuContext?.hasSidemenuItems
        || (Number(findifyDataContext?.workspace?.workspace_id) === 159 && (findifyDataContext?.workspace?.app_settings?.app_menu_lines?.length ?? 0) > 4);
    const shouldShowAccessibility = Number(findifyDataContext?.workspace?.workspace_id) === 159
        ? false
        : showAccessibility ?? findifyDataContext?.visitVersion === "accessible";
    const canGoBack = allowBackFallback && navigation?.canGoBack?.();
    const goBackOrHome = () => {
        if (canGoBack) {
            navigation.goBack();
            return;
        }
        navigation.navigate("Place");
    }
    const leaveMuseum = () => {
        findifyMenuContext?.setActiveMuseumSlug(null);
        findifyMenuContext?.setActiveMuseumTitle(null);
        findifyMenuContext?.setIsSidemenuOpen(false);
        navigation.navigate("RmcTerritoryHome");
    }
    const openQrScanner = () => {
        if (onRightPress) {
            onRightPress();
            return;
        }
        navigation.push("ScanQR");
    }

    return <View style={[
        styles.header,
        isMuseumMode ? styles.museumHeader : null,
        {
            backgroundColor: colors.background.withAlpha(245),
            borderBottomColor: colors.primary.withAlpha(65),
        }
    ]}>
        {isMuseumMode ? (
            <Pressable style={[styles.headerIconButton, styles.museumBackButton, { backgroundColor: colors.primary.get() }]} onPress={leaveMuseum}>
                <Icon name="arrow-back" size={23} color={colors.background.get()} />
            </Pressable>
        ) : canOpenFullMenu ? (
            <Pressable style={styles.headerIconButton} onPress={() => toggleSidemenu(findifyMenuContext)}>
                <Icon name="menu-outline" size={30} color={colors.primary.get()} />
            </Pressable>
        ) : canGoBack ? (
            <Pressable style={styles.headerIconButton} onPress={goBackOrHome}>
                <Icon name="chevron-back" size={26} color={colors.primary.get()} />
            </Pressable>
        ) : (
            <Pressable style={styles.headerIconButton} onPress={goBackOrHome}>
                <Icon name="chevron-back" size={26} color={colors.primary.get()} />
            </Pressable>
        )}

        <Pressable style={styles.brandBlock} onPress={isMuseumMode ? leaveMuseum : () => navigation.navigate("Place")}>
            {logo ? <Image source={{ uri: logo }} resizeMode="contain" style={styles.logo} /> : <View style={styles.logo} />}
            {isMuseumMode ? <View style={[styles.museumPill, { backgroundColor: colors.secondary.withAlpha(28), borderColor: colors.secondary.withAlpha(90) }]}><Icon name="location-outline" size={13} color={colors.primary.get()} /><PlaceText numberOfLines={1} style={[styles.museumPillText, { color: colors.primary.get() }]}>{museumTitle}</PlaceText></View> : null}
        </Pressable>

        <View style={styles.headerActions}>
            {shouldShowAccessibility ? (
                <Pressable style={[styles.accessibilityButton, { borderColor: colors.secondary.get() }]}>
                    <Icon name="accessibility" size={24} color={colors.primary.get()} />
                </Pressable>
            ) : null}
            <Pressable style={styles.headerIconButton} onPress={openQrScanner}>
                <Icon name={rightIcon} size={26} color={colors.primary.get()} />
            </Pressable>
        </View>
    </View>
}

const styles = StyleSheet.create({
    header: {
        height: 56,
        borderBottomWidth: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 24,
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
        zIndex: 5,
    },
    museumHeader: {
        height: 72,
        paddingHorizontal: 16,
    },
    headerIconButton: {
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
    },
    logo: {
        width: 144,
        height: 34,
    },
    brandBlock: {
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        maxWidth: "62%",
    },
    museumBackButton: {
        borderRadius: 21,
    },
    museumPill: {
        maxWidth: 190,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 99,
        borderWidth: 1,
    },
    museumPillText: {
        fontSize: 10,
        fontWeight: "800",
    },
    headerActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        minWidth: 42,
        justifyContent: "flex-end",
    },
    accessibilityButton: {
        width: 42,
        height: 42,
        borderRadius: 11,
        borderWidth: 1,
        alignItems: "center",
        justifyContent: "center",
    },
});
